import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, Clock, FileText, Handshake, Landmark, LineChart, MapPin, Percent, Building2, Globe, Mail, ShieldCheck } from "lucide-react";
import { ArticleCard } from "@/components/article-card";
import { EditorialHero as StandardsHero } from "@/components/editorial-page-kit";
import { ListPagination } from "@/components/list-pagination";
import { categoryHeroMeta } from "@/lib/category-view";
import { CACHE, setEdgeCache } from "@/lib/http-cache";
import { SITE, ROBOTS_INDEX } from "@/lib/site";
import { ogImageMeta } from "@/lib/og";
import { getDealsHub } from "@/lib/deals-hub.functions";
import type { DealsHubData } from "@/lib/deals-hub.server";
import { EVENT_TYPE_LABELS, EVENT_STATUS_LABELS, type TrackerEvent } from "@/lib/tracker/taxonomy";

const TITLE = `Asia Retail M&A: Deals, Acquisitions and Investments | ${SITE.name}`;
const DESCRIPTION =
  "Every retail merger, acquisition, buyout, stake sale and investment we report across Asia, in one place, with the structured record behind each deal in the Deal & Expansion Tracker.";
const URL = `${SITE.url}/deals`;

const PER_PAGE = 9;


const DEAL_TYPES = [
  {
    icon: Handshake,
    title: "Mergers & acquisitions",
    body: "Full takeovers and agreed mergers — the transactions that redraw market share overnight, from convenience-store consolidation in Japan to grocery platform roll-ups in Southeast Asia.",
  },
  {
    icon: Percent,
    title: "Stake sales & carve-outs",
    body: "Minority and majority positions, strategic investments and non-core divestments. Often the first signal that a larger reshuffle is coming, so we track them as closely as outright sales.",
  },
  {
    icon: Landmark,
    title: "Buyouts & take-privates",
    body: "Private equity moves on listed retailers, founder buybacks and delistings. These deals change who answers for strategy — and usually precede a restructuring.",
  },
  {
    icon: LineChart,
    title: "Funding rounds",
    body: "Growth capital for retail and consumer platforms. We cover rounds that change a company's expansion capacity, not seed cheques for pre-launch concepts.",
  },
];

const METHODOLOGY = [
  {
    icon: ShieldCheck,
    title: "Primary sources first",
    body: "Exchange filings, regulator notices and company statements before press aggregation. If it is only reported, we say so.",
  },
  {
    icon: FileText,
    title: "Structured record",
    body: "Every deal is logged in the Deal & Expansion Tracker with parties, markets, value, status and a link to the original story.",
  },
  {
    icon: BadgeCheck,
    title: "Follow-through",
    body: "Deals do not end at announcement. We track approvals, completion and the integrations that decide whether they worked.",
  },
];

export const Route = createFileRoute("/deals")({
  loader: async (): Promise<DealsHubData> => {
    await setEdgeCache(CACHE.listing);
    try {
      return (await getDealsHub()) as DealsHubData;
    } catch {
      return { posts: [], events: [], updatedAt: "" };
    }
  },
  head: () => ({
    meta: [
      ...ogImageMeta(),
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: ROBOTS_INDEX },
      { property: "og:title", content: TITLE },
      { name: "twitter:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { name: "twitter:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Asia retail M&A deals",
          description: DESCRIPTION,
          url: URL,
          isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
            { "@type": "ListItem", position: 2, name: "Asia retail M&A", item: URL },
          ],
        }),
      },
    ],
  }),
  component: DealsHubPage,
});

function SectionHeading({ children, count }: { children: string; count?: string }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4 border-b border-hairline pb-3">
      <div>
        <p className="category-eyebrow">{children}</p>
        <div className="mt-2 h-1 w-16 rounded-full bg-gradient-to-r from-brand-deep via-brand to-brand-light" />
      </div>
      {count ? (
        <span className="font-sans text-[11px] uppercase tracking-wide text-ink-muted">{count}</span>
      ) : null}
    </div>
  );
}

function EventRow({ event }: { event: TrackerEvent }) {
  return (
    <li className="border-b border-hairline last:border-0">
      <Link
        to="/tracker/$slug"
        params={{ slug: event.slug }}
        className="group block px-5 py-4 transition hover:bg-brand/5"
      >
        <div className="flex flex-wrap items-center gap-2 font-sans text-[10px] uppercase tracking-wide text-ink-muted">
          <span className="rounded-full bg-brand/10 px-2 py-0.5 font-bold text-brand">
            {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
          </span>
          <span>{EVENT_STATUS_LABELS[event.status] ?? event.status}</span>
          {event.announced_at ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3 text-brand" aria-hidden="true" />
              {event.announced_at}
            </span>
          ) : null}
        </div>
        <h3 className="mt-2 font-serif text-base font-bold leading-snug group-hover:text-brand">
          {event.headline}
        </h3>
        {event.markets?.length ? (
          <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-muted">
            <MapPin className="h-3 w-3 text-brand" aria-hidden="true" />
            {event.markets.join(", ")}
          </p>
        ) : null}
      </Link>
    </li>
  );
}

const KEEP_READING = [
  { to: "/companies", icon: Building2, label: "Company hubs", note: "Every story per retailer" },
  { to: "/markets", icon: Globe, label: "Market desks", note: "Coverage country by country" },
  { to: "/people-moves", icon: Clock, label: "People moves", note: "Who runs it after the deal" },
  { to: "/newsletter", icon: Mail, label: "Newsletter", note: "Deals in your inbox" },
] as const;

function DealsHubPage() {
  const { posts, events } = Route.useLoaderData();
  const [page, setPage] = useState(1);

  const [hero, ...rest] = posts;
  const pageCount = Math.max(1, Math.ceil(rest.length / PER_PAGE));
  const current = Math.min(page, pageCount);
  const slice = rest.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  return (
    <>
      <StandardsHero
        eyebrow="Asia retail M&A"
        icon={<Handshake className="h-5 w-5" strokeWidth={2} />}
        title="Who is buying whom in Asian retail"
        lede="Acquisitions, mergers, buyouts, stake sales and funding rounds that change who owns the shelf — from convenience chains and grocers to bubble tea, beauty and e-commerce."
        meta={categoryHeroMeta(posts, posts.length || undefined, 0)}
      />

      <main className="container-editorial py-10">
        <section aria-labelledby="deals-intro" className="pb-10">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div>
              <p id="deals-intro" className="category-eyebrow">About this desk</p>
              <h2 className="mt-2 font-serif text-2xl font-bold leading-tight">
                Ownership is the story behind every other retail story
              </h2>
              <div className="mt-4 max-w-prose space-y-4 text-[15px] leading-relaxed text-ink-muted">
                <p>
                  When a convenience chain in Tokyo changes hands, when a Southeast Asian grocery
                  group sells a stake to a global fund, when a bubble-tea brand raises a round it
                  plans to spend on store openings — those are not finance stories. They are the
                  reason ranges change, stores close, suppliers get swapped and executives move.
                </p>
                <p>
                  This page gathers every ownership story we publish across Asia in one place, and
                  connects each one to its structured record in the Deal &amp; Expansion Tracker —
                  parties, markets, value, status and source — so a headline you read here becomes
                  an entry you can act on there.
                </p>
              </div>
            </div>
            <ul className="space-y-4 self-start border border-hairline bg-card p-5">
              {METHODOLOGY.map((m) => (
                <li key={m.title} className="flex items-start gap-3">
                  <m.icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                  <span>
                    <span className="block font-serif text-sm font-bold">{m.title}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-ink-muted">{m.body}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="deal-types" className="border-t border-hairline py-10">
          <p id="deal-types" className="category-eyebrow">What we track</p>
          <div className="mt-6 grid gap-px overflow-hidden border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {DEAL_TYPES.map((t) => (
              <div key={t.title} className="bg-card p-5">
                <t.icon className="h-5 w-5 text-brand" aria-hidden="true" />
                <h3 className="mt-3 font-serif text-base font-bold leading-snug">{t.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-ink-muted">{t.body}</p>
              </div>
            ))}
          </div>
        </section>

        {posts.length ? (
          <>
            <section aria-label="Lead deal" className="pb-10">
              <ArticleCard post={hero} variant="hero" priority headingLevel={2} />
            </section>

            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_340px]">
              <section aria-labelledby="deal-coverage">
                <SectionHeading count={`${rest.length} stories`}>Latest deal coverage</SectionHeading>
                <div className="grid gap-8 sm:grid-cols-2">
                  {slice.map((post, i) => (
                    <ArticleCard key={post.id} post={post} showExcerpt priority={i < 2} />
                  ))}
                </div>
                <div className="mt-10">
                  <ListPagination
                    page={current}
                    pageCount={pageCount}
                    onPageChange={setPage}
                    label="Deal coverage pagination"
                  />
                </div>
              </section>

              <aside className="space-y-8">
                <div className="overflow-hidden border border-hairline bg-card">
                  <div className="relative overflow-hidden bg-gradient-to-br from-brand-deep via-brand to-brand-light px-5 py-6 text-white">
                    <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] opacity-90">
                      Structured record
                    </p>
                    <h2 className="mt-2 font-serif text-xl font-bold leading-tight">
                      In the Deal &amp; Expansion Tracker
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/85">
                      Parties, market, value, status and the source article — for every deal we report.
                    </p>
                  </div>
                  {events.length ? (
                    <ul>
                      {events.map((event) => (
                        <EventRow key={event.id} event={event} />
                      ))}
                    </ul>
                  ) : null}
                  <div className="border-t border-hairline px-5 py-4">
                    <Link
                      to="/tracker"
                      className="inline-flex items-center gap-2 font-sans text-xs font-bold uppercase tracking-[0.12em] text-brand hover:underline"
                    >
                      Open the tracker
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>

                <div className="border border-hairline bg-card p-5">
                  <p className="category-eyebrow">Keep reading</p>
                  <ul className="mt-4 divide-y divide-hairline">
                    {KEEP_READING.map((item) => (
                      <li key={item.to}>
                        <Link to={item.to} className="group flex items-start gap-3 py-3">
                          <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                          <span className="min-w-0">
                            <span className="block font-serif text-sm font-bold leading-snug group-hover:text-brand">
                              {item.label}
                            </span>
                            <span className="block text-xs text-ink-muted">{item.note}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            </div>
          </>
        ) : (
          <p className="text-ink-muted">
            Deal coverage is loading. Try the{" "}
            <Link to="/search" className="underline">
              search page
            </Link>{" "}
            in the meantime.
          </p>
        )}

      </main>

    </>
  );
}
